import React, { useState, useEffect } from 'react';
import styles from './ReportersList.module.scss';
import PaginationControls from '../PaginationControls/PaginationControls';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import env from '@jwp/ott-common/src/env';
import DefaultAvatar from '@jwp/ott-theme/assets/profiles/avatar.png';
import {
    GET_REPORT_CASE_REPORTERS,
    GetReportCaseReportersData,
    GetReportCaseReportersVariables,
} from '@jwp/ott-common/src/graphql/query/reportModeration.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';

const ClockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

// Custom Debounce Hook
function useDebounce<T>(value: T, delay: number = 500): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

interface ReportersListProps {
    rcId?: string;
}

const ReportersList: React.FC<ReportersListProps> = ({ rcId: propRcId }) => {
    const { t } = useTranslation(['user', 'video', 'common', 'upload']);
    const params = useParams<{ id?: string; rcId?: string }>();

    const activeRcId = propRcId || params.rcId || params.id || '';

    // Search States
    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce<string>(searchQuery, 400); // 400ms delay

    // Pagination States
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);

    // Reset page to 1 when debounced search actually triggers
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery]);

    const offset = (currentPage - 1) * itemsPerPage;

    // API Query with Debounced Search Query
    const { data, loading, error } = useQuery<
        GetReportCaseReportersData,
        GetReportCaseReportersVariables
    >(GET_REPORT_CASE_REPORTERS, {
        client: getAuthApolloClient(),
        variables: {
            input: {
                rcId: activeRcId,
                limit: itemsPerPage,
                offset: offset,
                search: debouncedSearchQuery, // Passed debounced value here
            },
        },
        skip: !activeRcId,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    });

    const reportersList = data?.getReportCaseReporters?.items || [];
    const totalItems = data?.getReportCaseReporters?.totalCount || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (items: number) => {
        setItemsPerPage(items);
        setCurrentPage(1);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);

        const datePart = date.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
        });

        const timePart = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });

        return `${datePart} | ${timePart}`;
    };

    return (
        <div className={styles.reportersContainer}>
            <div className={styles.headerContainer}>
                <h2 className={styles.sectionTitle}>
                    {t('user:report_details.reporters', { defaultValue: 'Reporters' })}
                </h2>

                <div className={styles.searchBox}>
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder={t('user:report_details.search_placeholder', { defaultValue: 'Search for reporters...' })}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)} // Smooth UI updates
                    />
                </div>
            </div>

            <div className={styles.reportersList}>
                {loading ? (
                    <div className={styles.noResults}>Loading reporters...</div>
                ) : error ? (
                    <div className={styles.noResults}>Error loading reporters: {error.message}</div>
                ) : reportersList.length > 0 ? (
                    reportersList.map((item) => {
                        const reporterAvatarSrc = item.reporterInfo?.uAvatarUrl
                            ? `${env.APP_BASE_URL}${item.reporterInfo.uAvatarUrl}`
                            : DefaultAvatar;

                        return (
                            <div key={item.rId} className={styles.reporterCard}>
                                <img
                                    src={reporterAvatarSrc}
                                    alt={item.reporterInfo?.uFullName || 'Reporter'}
                                    className={styles.avatar}
                                />

                                <div className={styles.content}>
                                    <div className={styles.headerRow}>
                                        <span className={styles.reporterName}>
                                            {item.reporterInfo?.uFullName || 'Anonymous'}
                                        </span>
                                        <span className={`${styles.badge} ${styles[item.reasonCode?.toLowerCase() || 'default']}`}>
                                            {item.reasonLabel}
                                        </span>
                                    </div>
                                    <p className={styles.reasonText}>
                                        {item.details ? `"${item.details}"` : `Reason Code: ${item.reasonCode}`}
                                    </p>
                                </div>

                                <div className={styles.timestamp}>
                                    <ClockIcon />
                                    <span>{formatDate(item.createdAt)}</span>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className={styles.noResults}>No reporters found</div>
                )}
            </div>

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className={styles.paginationOverrides}
            />
        </div>
    );
};

export default ReportersList;